import type { FC } from 'react';
import React, { useMemo } from 'react';
import { createContext } from '@shared/utils/createContext';
import type { GuestSiteEventDesign } from '../layout.types';
import { useTheme } from '@withjoy/joykit';
import { deriveWebsiteColorsFromEventDesign, parseDesignPreferences } from '@shared/utils/websiteDesign';
import { DesignLayoutType } from '@graphql/generated-base';

type ColorPaletteProviderContext = Readonly<{
  accentColor?: string;
  artBackgroundColor?: string;
  pageFontColor?: string;
  pageColor?: string;
  artBackgroundFontColor?: string;
  rsvpButtonColor?: string;
  brannanInnerFrameBorder?: boolean;
  /**
   * Represents the event's theme identifier.
   * Undefined when no event design is available.
   */
  themeId?: string;
  /**
   * Whether the event is using a custom uploaded theme/art.
   * When true full screen width features for navbar and accommodations map should not be enabled.
   */
  isCustomArtApplied?: boolean;
}>;

type ColorPaletteProviderProps = Readonly<{
  eventDesign: GuestSiteEventDesign;
}>;

const [Provider, useColorPaletteProvider] = createContext<ColorPaletteProviderContext>({
  name: 'ColorPaletteProvider',
});

const ColorPaletteProvider: FC<ColorPaletteProviderProps> = ({ children, eventDesign }) => {
  const theme = useTheme();

  const ctx = useMemo<ColorPaletteProviderContext>(() => {
    if (eventDesign) {
      const derivedWebsiteColors = deriveWebsiteColorsFromEventDesign(eventDesign, eventDesign.theme);
      const rsvpButtonColor = eventDesign.websiteLayout.layoutType === DesignLayoutType.aloha ? derivedWebsiteColors.artBackgroundFontColor : derivedWebsiteColors.accentColor;
      // Extract brannanInnerFrameBorder from design preferences (configurable in admin color settings)
      const { brannanInnerFrameBorder } = parseDesignPreferences(eventDesign.designPreferences);

      return {
        ...derivedWebsiteColors,
        rsvpButtonColor: rsvpButtonColor || theme.colors.black,
        brannanInnerFrameBorder: brannanInnerFrameBorder?.booleanValue,
        themeId: eventDesign.theme?.themeId,
        isCustomArtApplied: eventDesign.useCustomTheme,
      };
    }

    return {
      accentColor: undefined,
      artBackgroundColor: undefined,
      artBackgroundFontColor: undefined,
      pageFontColor: undefined,
      pageColor: undefined,
      brannanInnerFrameBorder: undefined,
      themeId: undefined,
      isCustomArtApplied: undefined,
    };
  }, [eventDesign, theme]);

  return <Provider value={ctx}>{children}</Provider>;
};

ColorPaletteProvider.displayName = 'ColorPaletteProvider';

export { ColorPaletteProvider, useColorPaletteProvider };
